import React, { useState, ReactNode, Dispatch, SetStateAction } from 'react';

import { createContext, useContextSelector, useContext } from 'use-context-selector';
import { UnitInfoProps } from '../models/UnitInfo';
import { useGenericModals } from '../store/genericModals';
import { CartContext } from './CartContext';
import { PatientContext } from './PatientContext';
import { PaymentContext } from './PaymentContext';
import { RouterContext } from './RouterContext';
import { ScheduleContext } from './ScheduleContext';
import { ServiceContext } from './ServiceContext';
import { SpecialistContext } from './SpecialistContext';

export type PlatformContextProps = {
  id: string;
  setId: Dispatch<SetStateAction<string>>;
  clearData: () => void;
  isSchedulerOpen: boolean;
  setIsSchedulerOpen: Dispatch<SetStateAction<boolean>>;
  selectedUnit: UnitInfoProps | undefined;
  setSelectedUnit: Dispatch<SetStateAction<UnitInfoProps | undefined>>;
  isModalCloseOpen?: boolean;
  setModalCloseOpen?: Dispatch<SetStateAction<boolean>>;
  fixedModule: boolean;
  setFixedModule: Dispatch<SetStateAction<boolean>>;
  isSDKAllowed: boolean;
  setIsSDKAllowed: Dispatch<SetStateAction<boolean>>;
};

type ServiceProviderProps = {
  children: ReactNode;
};

export const PlatformContext = createContext({} as PlatformContextProps);

export function PlatformProvider({ children }: ServiceProviderProps) {
  const [id, setId] = useState('');
  const [isSchedulerOpen, setIsSchedulerOpen] = useState(false);
  const [isModalCloseOpen, setModalCloseOpen] = useState<boolean>(false);
  const [fixedModule, setFixedModule] = useState<boolean>(false);
  const [selectedUnit, setSelectedUnit] = useState<UnitInfoProps>();
  const [isSDKAllowed, setIsSDKAllowed] = useState<boolean>(false);

  const { clearCart } = useContextSelector(CartContext, state => state);
  const { resetRoutes } = useContextSelector(RouterContext, state => state);
  const { clearSchedules, selectedItem } = useContextSelector(ScheduleContext, state => state);
  const { clearPatientList } = useContextSelector(PatientContext, state => state);
  const { clearSpecialistConntext } = useContextSelector(SpecialistContext, state => state);
  const { clearService, selectedService } = useContextSelector(ServiceContext, state => state);
  const { clearEligibilityPlans } = useContextSelector(PaymentContext, state => state);
  const clearGenericModals = useGenericModals(state => state.clearGenericModals);

  const clearData = () => {
    if (!fixedModule) clearPatientList();

    clearCart();
    clearSchedules();
    clearService();
    resetRoutes();
    clearEligibilityPlans();
    clearGenericModals();
    clearSpecialistConntext();
  };

  return (
    <PlatformContext.Provider
      value={{
        id,
        setId,
        clearData,
        isSchedulerOpen,
        setIsSchedulerOpen,
        selectedUnit,
        setSelectedUnit,
        isModalCloseOpen,
        setModalCloseOpen,
        fixedModule,
        setFixedModule,
        isSDKAllowed,
        setIsSDKAllowed
      }}
    >
      {children}
    </PlatformContext.Provider>
  );
}

export function usePlatformContext() {
  return useContext(PlatformContext);
}
